import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Instagram, Download, Copy, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ShareModal = ({ isOpen, onClose, affirmation }) => {
  const canvasRef = useRef(null);
  const { toast } = useToast();

  const generateShareImage = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 1080;
    canvas.height = 1080;
    
    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#fdf2f8');
    gradient.addColorStop(1, '#fce7f3');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add border
    ctx.strokeStyle = '#f9a8d4';
    ctx.lineWidth = 8;
    ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);
    
    // Add brand watermark
    ctx.font = 'bold 36px Arial';
    ctx.fillStyle = '#be185d';
    ctx.textAlign = 'center';
    ctx.fillText('CONSTANT LOVE', canvas.width / 2, 120);
    
    // Add affirmation text
    ctx.font = '42px Arial';
    ctx.fillStyle = '#374151';
    ctx.textAlign = 'center';
    
    // Word wrap for affirmation
    const words = affirmation.affirmation.split(' ');
    const maxWidth = 900;
    let line = '';
    let y = 400;
    
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      
      if (testWidth > maxWidth && n > 0) {
        ctx.fillText(line, canvas.width / 2, y);
        line = words[n] + ' ';
        y += 60;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, canvas.width / 2, y);
    
    // Add action step
    ctx.font = 'bold 28px Arial';
    ctx.fillStyle = '#be185d';
    ctx.fillText('💫 Action Step:', canvas.width / 2, y + 120);
    
    ctx.font = '32px Arial';
    ctx.fillStyle = '#374151';
    
    // Word wrap for action
    const actionWords = affirmation.action.split(' ');
    line = '';
    y += 180;
    
    for (let n = 0; n < actionWords.length; n++) {
      const testLine = line + actionWords[n] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      
      if (testWidth > maxWidth && n > 0) {
        ctx.fillText(line, canvas.width / 2, y);
        line = actionWords[n] + ' ';
        y += 50;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, canvas.width / 2, y);
    
    // Add bottom branding
    ctx.font = '24px Arial';
    ctx.fillStyle = '#9ca3af';
    ctx.fillText('Download Constant Love App', canvas.width / 2, canvas.height - 80);
    
    return canvas.toDataURL('image/png');
  };

  const handleDownload = () => {
    const dataURL = generateShareImage();
    const link = document.createElement('a');
    link.download = 'constant-love-affirmation.png';
    link.href = dataURL;
    link.click();
    
    toast({
      title: "Image Downloaded!",
      description: "Your affirmation image has been saved to your device.",
      duration: 3000,
    });
  };

  const handleCopyText = () => {
    const text = `"${affirmation.affirmation}"\n\n💫 ${affirmation.action}\n\n#ConstantLove #SelfLove #Affirmations`;
    navigator.clipboard.writeText(text);
    
    toast({
      title: "Text Copied!",
      description: "Affirmation text copied to clipboard.",
      duration: 3000,
    });
  };

  const handleInstagramShare = () => {
    toast({
      title: "Instagram Sharing",
      description: "Download the image and share it on your Instagram story or feed!",
      duration: 5000,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Share Your Affirmation
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-rose-50 p-4 rounded-lg">
            <p className="text-sm font-medium text-rose-800 mb-2">Preview:</p>
            <p className="text-sm text-gray-700 italic">"{affirmation.affirmation}"</p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <Button 
              onClick={handleDownload}
              className="bg-rose-500 hover:bg-rose-600 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            
            <Button 
              onClick={handleInstagramShare}
              variant="outline"
              className="border-pink-300 text-pink-600 hover:bg-pink-50"
            >
              <Instagram className="w-4 h-4 mr-2" />
              Instagram
            </Button>
          </div>
          
          <Button 
            onClick={handleCopyText}
            variant="outline"
            className="w-full border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy Text
          </Button>
        </div>
        
        <canvas 
          ref={canvasRef}
          style={{ display: 'none' }}
          width={1080}
          height={1080}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;